import type { RouteId } from "./index-table";
import { ROUTE_LIST } from "./index-table";
import type { RouteSpec } from "./route";

export interface MatchedRoute {
  spec: RouteSpec & { id: RouteId };
  params: Record<string, string>;
}

const compiled = ROUTE_LIST.map((spec) => {
  const names: string[] = [];
  const source = spec.path.replace(/[.*+?^${}()|[\]\\]/g, "\\$&").replace(/:([A-Za-z]+)/g, (_, n: string) => {
    names.push(n);
    return "([^/]+)";
  });
  return { spec, names, re: new RegExp(`^${source}$`) };
});

/**
 * Finds the contract route for a request line. Static paths win over parameter paths
 * (`/api/galleries/count` is not `/api/galleries/:gid`). Used by the fakes; the API uses Elysia's router.
 */
export function findRoute(method: string, pathname: string): MatchedRoute | null {
  let best: { m: MatchedRoute; params: number } | null = null;
  for (const c of compiled) {
    if (c.spec.method !== method.toUpperCase()) continue;
    const match = c.re.exec(pathname);
    if (!match) continue;
    const params: Record<string, string> = {};
    c.names.forEach((n, i) => {
      params[n] = decodeURIComponent(match[i + 1] as string);
    });
    if (!best || c.names.length < best.params) best = { m: { spec: c.spec, params }, params: c.names.length };
  }
  return best?.m ?? null;
}

/** `?a=1&a=2&b=3` -> `{ a: ["1","2"], b: "3" }` */
export function queryObject(url: URL): Record<string, string | string[]> {
  const out: Record<string, string | string[]> = {};
  for (const key of new Set(url.searchParams.keys())) {
    const all = url.searchParams.getAll(key);
    out[key] = all.length === 1 ? (all[0] as string) : all;
  }
  return out;
}
